import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useImmer } from 'use-immer';
import { Toast, Button, TextArea, Avatar, Modal, Switch, Dialog } from 'antd-mobile';
import { RedoOutline } from 'antd-mobile-icons';
import { useI18n } from '../../../hooks/useI18n';
import { request } from '../../../lib/api';
import { getScenarioDetail, ScenarioDetail } from '../../../lib/api/scenario';
import { 
  getSessionMessages, 
  getSessionDetailById, 
  type MessageInfo, 
} from "../../../lib/api/conversations";
import { useWebSocket } from '../../../hooks/useWebSocket';
import { v4 as uuidv4 } from 'uuid';
import { sleep } from '@/utils/toolUtils';
import { formatTextWithLineBreaks } from '@/utils/textUtils';

// 消息接口定义
interface Message {
  id: string;
  type: "user" | "ai";
  content: string;
  isComplete?: boolean;
  timestamp?: number;
  isStreaming?: boolean;
}

// 会话接口定义
interface Session {
  id: string;
  created_at: string;
  updated_at: string;
}

// 学员信息接口
interface StudentInfo {
  id: string;
  name: string;
  description?: string;
  role_requirements?: string;
  additional_info?: string;
  is_active: boolean;
}

// AI陪练、对话相关事件类型常量
const CHAT_EVENTS = {
  CHAT_MESSAGE: "chat_message",
  CHAT_STREAM: "chat_message_stream",
  CHAT_STREAM_END: "chat_message_stream_end",
  UPDATE_MESSAGE_DATA: "update_message_data",
  UPDATE_SESSION_DATA: "update_session_data",
  CHAT_WRONG_NOTICE: "chat_wrong_notice",
  CHAT_COMPLETE: "chat_complete",
  CHAT_FORCE_END: "chat_force_end",
  DEVIATION_ANALYSIS: "deviation_analysis",
  AI_ASSISTANCE_HINT: "ai_assistance_hint",
  ERROR: "error",
  CHAT_TIMEOUT_END: "chat_timeout_end",
  CHAT_ALLSTEPS_END: "chat_allsteps_end",
} as const;

type ChatEventType = (typeof CHAT_EVENTS)[keyof typeof CHAT_EVENTS];

const AiTrainingPreview: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const [searchParams] = useSearchParams();
  const student_id = searchParams.get('student_id');
  
  // 基础状态
  const [scenarioData, setScenarioData] = useState<ScenarioDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useImmer<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const chatContentRef = useRef<HTMLDivElement>(null);
  
  // 选择状态
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const selectedLanguage = 'zh';
  
  // 会话状态
  const [session, setSession] = useState<Session | null>(null);
  const [isSessionEnded, setIsSessionEnded] = useState(false); // 会话是否已结束
  
  // 学员相关状态
  const [studentInfo, setStudentInfo] = useState<StudentInfo | null>(null);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [isAutoReplying, setIsAutoReplying] = useState(false);
  const autoReplyTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 原始提示词相关状态
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [promptData, setPromptData] = useState<any>(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  
  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    if (chatContentRef.current) {
      setTimeout(() => {
        if (chatContentRef.current) {
          chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
        }
      }, 100);
    }
  }, []);
  
  // 获取学员信息
  const fetchStudentInfo = async () => {
    if (!student_id) return;
    
      try {
        const response = await request(`/scene-students/students/${student_id}`, {
          method: 'GET',
        });
      
      if (response.success && response.data) {
        setStudentInfo(response.data);
        setAutoReplyEnabled(true); // 默认开启自动回复
      }
    } catch (error) {
      console.error('获取学员信息失败:', error);
      Toast.show({ content: '获取学员信息失败', icon: 'fail' });
    }
  };
  
  // 获取场景详情
  const fetchScenarioDetail = async () => {
    if (!scenarioId) return;
    setLoading(true);
    try {
      const response = await getScenarioDetail(scenarioId);
      if (response.data) {
        setScenarioData(response.data);
        if (response.data.ai_roles && response.data.ai_roles.length > 0) {
          setSelectedRoleId(response.data.ai_roles[0].scenario_role_id);
        }
      }
    } catch (error) {
      Toast.show({ content: t('ai.fetchScenarioFailed', '获取场景失败'), icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };
  
  // 将MessageInfo转换为Message格式
  const convertMessageInfoToMessage = (messageInfo: MessageInfo): Message => {
    return {
      id: messageInfo.id,
      type: messageInfo.speaker === "user" ? "user" : "ai",
      content: messageInfo.content,
      isComplete: true,
      timestamp: new Date(messageInfo.timestamp).getTime(),
      isStreaming: false,
    };
  };

  // 获取会话历史消息
  const fetchSessionHistory = async (sessionId: string) => {
    try {
      const messagesResponse = await getSessionMessages(sessionId, {
        page: 1,
        pageSize: 100,
      });
      
      if (
        messagesResponse.success &&
        messagesResponse.data &&
        messagesResponse.data.list
      ) {
        const historyMessages = messagesResponse.data.list.map(
          convertMessageInfoToMessage
        );
        
        if (historyMessages.length > 0) {
          setMessages((draft) => {
            draft.splice(0, draft.length, ...historyMessages);
          });
        } else {
          addInitialMessage();
        }
      } else {
        addInitialMessage();
      }
    } catch (error) {
      console.error("获取历史消息失败:", error);
      addInitialMessage();
    }
  };

  // 添加初始消息
  const addInitialMessage = () => {
    const initialMessage: Message = {
      id: uuidv4(),
      type: "ai",
      content: scenarioData?.opening_dialogue || t('ai.chat.welcomeMessage', "你好！欢迎来到AI陪练，我是你的AI助手。让我们开始今天的练习吧！"),
      isComplete: true,
      timestamp: Date.now(),
    };
    setMessages((draft) => {
      draft.splice(0, draft.length);
      draft.push(initialMessage);
    });
  };
  
  // 处理流式消息块
  const handleChatMessageStream = useCallback((message: any) => {
    const streamData = message.data;
    const messageId = streamData.message_id;
    const chunk = streamData.chunk || "";
    
    if (chunk) {
      setMessages((prevState) => {
        const draft = [...prevState];
        const existingMessageIndex = draft.findIndex(
          (msg: Message) => msg.id === messageId
        );
        
        if (existingMessageIndex !== -1) {
          draft[existingMessageIndex] = {
            ...draft[existingMessageIndex],
            content: draft[existingMessageIndex].content + chunk,
            isStreaming: true,
            isComplete: false,
          };
        } else {
          const streamingIndex = draft.findIndex(
            (msg: Message) => msg.isStreaming
          );
          if (streamingIndex !== -1) {
            draft.splice(streamingIndex, 1);
          }
          const newStreamingMessage: Message = {
            id: messageId,
            content: chunk,
            type: streamData.is_user ? "user" : "ai",
            isComplete: false,
            isStreaming: true,
            timestamp: Date.now(),
          };
          draft.push(newStreamingMessage);
        }
        return draft;
      });
      setShowLoading(false);
    }
  }, []);
  
  // 处理流式消息结束
  const handleChatMessageStreamEnd = useCallback((message: any) => {
    const messageId = message.data.message_id;
    const finalContent = message.data.content;
    
    console.log("流式消息结束:", messageId, finalContent);
    
    setMessages((draft) => {
      const messageIndex = draft.findIndex(
        (msg: Message) => msg.id === messageId
      );
      if (messageIndex !== -1) {
        draft[messageIndex].content = finalContent || draft[messageIndex].content;
        draft[messageIndex].isStreaming = false;
        draft[messageIndex].isComplete = true;
      }
    });
    
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [scrollToBottom]);
  
  // 处理错误消息
  const handleErrorMessage = useCallback((message: any) => {
    console.error("WebSocket错误消息:", message.data);
    setShowLoading(false);
    setMessages((draft) => {
      const streamingIndex = draft.findIndex((msg: Message) => msg.isStreaming);
      if (streamingIndex !== -1) {
        draft.splice(streamingIndex, 1);
      }
    });
  }, []);
  
  // 处理对话完成事件
  const handleChatComplete = useCallback((message: any) => {
    console.log("收到对话完成事件:", message.data);
    
    setIsSessionEnded(true);
    setShowLoading(false);
    setAutoReplyEnabled(false);
    
    // 清除自动回复定时器
    if (autoReplyTimerRef.current) {
      clearTimeout(autoReplyTimerRef.current);
      autoReplyTimerRef.current = null;
    }
    
    Toast.show({
      content: t("ai.chat.practiceEndedThanks", "对练已结束，感谢你的参与！"),
      duration: 3000,
    });
  }, [t]);
  
  // 处理强制结束事件
  const handleChatForceEnd = useCallback((message: any) => {
    console.log("收到强制结束事件:", message.data);
    
    setIsSessionEnded(true);
    setShowLoading(false);
    setAutoReplyEnabled(false);
    
    // 清除自动回复定时器
    if (autoReplyTimerRef.current) {
      clearTimeout(autoReplyTimerRef.current);
      autoReplyTimerRef.current = null;
    }
    
    Dialog.alert({
      content: t('ai.chat.forceEndMessage', '对话已被系统强制结束'),
      confirmText: t('common.understood', '知道了'),
    });
  }, [t]);
  
  // 处理超时结束事件
  const handleChatTimeoutEnd = useCallback((message: any) => {
    const data = message.data;
    console.log("收到超时结束事件:", data);
    
    setIsSessionEnded(true);
    setShowLoading(false);
    setAutoReplyEnabled(false);
    
    // 清除自动回复定时器
    if (autoReplyTimerRef.current) {
      clearTimeout(autoReplyTimerRef.current);
      autoReplyTimerRef.current = null;
    }
    
    Dialog.alert({
      title: t('ai.chat.timeoutEndTitle', '对话超时结束'),
      content: data.message || t('ai.chat.timeoutEndMessage', '对话已超时结束，无法继续对话。'),
      confirmText: t('common.understood', '知道了'),
    });
  }, [t]);
  
  // 处理完成所有环节结束事件
  const handleChatAllStepsEnd = useCallback((message: any) => {
    const data = message.data;
    console.log("收到完成所有环节结束事件:", data);
    
    setIsSessionEnded(true);
    setShowLoading(false);
    setAutoReplyEnabled(false);
    
    // 清除自动回复定时器
    if (autoReplyTimerRef.current) {
      clearTimeout(autoReplyTimerRef.current);
      autoReplyTimerRef.current = null;
    }
    
    Dialog.alert({
      title: t('ai.chat.恭喜完成！', '恭喜完成！'),
      content: data.message || t('ai.chat.恭喜！您已完成所有陪练环节。', '恭喜！您已完成所有陪练环节。'),
      confirmText: t('common.understood', '知道了'),
    });
  }, [t]);
  
  // WebSocket消息处理回调
  const handleWebSocketMessage = useCallback((wsData: any) => {
    if (wsData.content.type === "ping" || wsData.content.type === "pong") {
      return;
    }
    
    if (wsData.content.type !== "chat_message_stream") {
      console.log("收到WebSocket消息====》:", wsData);
    }
    
    if (!(wsData.content && wsData.content.type)) return;
    
    const message = wsData.content;
    
    switch (message.type) {
      case CHAT_EVENTS.CHAT_MESSAGE:
        console.log("收到聊天消息:", message.data);
        break;
        
      case CHAT_EVENTS.CHAT_STREAM:
        if (message.data) {
          handleChatMessageStream(message);
        }
        break;
        
      case CHAT_EVENTS.CHAT_STREAM_END:
        if (message.data) {
          handleChatMessageStreamEnd(message);
        }
        break;
        
      case CHAT_EVENTS.CHAT_COMPLETE:
        handleChatComplete(message);
        break;
        
      case CHAT_EVENTS.CHAT_FORCE_END:
        handleChatForceEnd(message);
        break;
        
      case CHAT_EVENTS.CHAT_TIMEOUT_END:
        handleChatTimeoutEnd(message);
        break;
        
      case CHAT_EVENTS.CHAT_ALLSTEPS_END:
        handleChatAllStepsEnd(message);
        break;
        
      case CHAT_EVENTS.ERROR:
        handleErrorMessage(message);
        break;
        
      default:
        console.warn("未处理的消息类型:", message.type);
        break;
    }
  }, [
    handleChatMessageStream, 
    handleChatMessageStreamEnd, 
    handleErrorMessage,
    handleChatComplete,
    handleChatForceEnd,
    handleChatTimeoutEnd,
    handleChatAllStepsEnd,
  ]);
  
  // WebSocket配置
  const webSocketConfig = useMemo(() => ({
    url: "",
    autoConnect: !!session?.id,
    debug: true,
    handleOnlineStatus: false,
    onMessage: handleWebSocketMessage,
  }), [session?.id, handleWebSocketMessage]);
  
  // WebSocket连接
  const { isConnected, send: sendWsMessage } = useWebSocket(webSocketConfig);
  
  // 创建会话
  const createSession = async () => {
    if (!selectedRoleId || !scenarioId) {
      Toast.show({ content: t('请选择角色'), icon: 'fail' });
      return;
    }
    
    setIsConnecting(true);
    try {
      const sessionResponse = await request<{
        session_id: string;
        session_token: string;
        status: string;
      }>('/conversations/scenario-sessions', {
        method: 'POST',
        data: {
          scenario_id: scenarioId,
          language_code: selectedLanguage,
          ai_role_id: selectedRoleId,
          source: student_id ? 'student_training' : 'test',
        }
      });
      
      if (sessionResponse.success && sessionResponse.data) {
        const newSessionId = sessionResponse.data.session_id;
        
        // 如果有学员ID，创建关联
          if (student_id) {
            try {
              await request('/scene-students/session-students', {
                method: 'POST',
                data: {
                  session_id: newSessionId,
                  student_id: student_id,
                  scenario_id: scenarioId,
                },
              });
              console.log('学员会话关联创建成功');
            } catch (error) {
              console.error('创建学员会话关联失败:', error);
              // 不影响主流程
            }
          }
        
        let sessionData: Session = {
            id: newSessionId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };

        try {
           const detailRes = await getSessionDetailById(newSessionId);
           if (detailRes.success && detailRes.data) {
             sessionData = {
               id: newSessionId,
               created_at: detailRes.data.created_at,
               updated_at: detailRes.data.updated_at || new Date().toISOString(),
             };
           }
        } catch (e) {
            console.warn("获取会话详情失败，使用默认值");
        }

        setSession(sessionData);
        await fetchSessionHistory(newSessionId);
        Toast.show({ content: t('会话创建成功'), icon: 'success' });
      } else {
        Toast.show({ content: t('创建会话失败'), icon: 'fail' });
      }
    } catch (error) {
      console.error('创建会话失败:', error);
      Toast.show({ content: t('创建会话失败，请重试'), icon: 'fail' });
    } finally {
      setIsConnecting(false);
    }
  };
  
  // 重新开始对话
  const restartConversation = () => {
    setSession(null);
    setMessages([]);
    setInputText('');
    setIsSessionEnded(false);
    setAutoReplyEnabled(false);
    
    // 清除自动回复定时器
    if (autoReplyTimerRef.current) {
      clearTimeout(autoReplyTimerRef.current);
      autoReplyTimerRef.current = null;
    }
  };
  
  // 手动结束对话
  const handleFinishSession = useCallback(() => {
    if (!session?.id || !isConnected) {
      console.warn("无法结束对话: 会话未创建或WebSocket未连接");
      return;
    }
    
    Dialog.confirm({
      title: t("ai.chat.confirmEndPractice", "确定要结束本次对练吗？"),
      content: t('ai.chat.endPreviewTip', '结束后将无法继续当前对话。'),
      confirmText: t("common.confirm", "确定结束"),
      cancelText: t("common.cancel", "继续对练"),
      onConfirm: () => {
        try {
          // 发送聊天结束事件到后端
          const finishData = {
            type: CHAT_EVENTS.CHAT_COMPLETE,
            data: {
              session_id: session.id,
              end_time: new Date().toISOString(),
              reason: "user_finish", // 用户主动结束
            },
            session_id: session.id,
            message_id: uuidv4(),
          };
          
          console.log("发送聊天结束事件:", finishData);
          sendWsMessage(finishData);
          
          // 立即更新本地状态
          setIsSessionEnded(true);
          setShowLoading(false);
          setAutoReplyEnabled(false);
          
          // 清除自动回复定时器
          if (autoReplyTimerRef.current) {
            clearTimeout(autoReplyTimerRef.current);
            autoReplyTimerRef.current = null;
          }
        } catch (error) {
          console.error("结束对话失败:", error);
          Toast.show({ content: t('结束对话失败'), icon: 'fail' });
        }
      },
    });
  }, [session, isConnected, sendWsMessage, t]);
  
  // 显示排查链接弹窗
  const handleCopyDebugLink = useCallback(() => {
    if (!session?.id) {
      Toast.show({ content: t('会话未创建'), icon: 'fail' });
      return;
    }
    
    const debugUrl = `${window.location.origin}/ai-training/debug?id=${session.id}`;
    
    Dialog.alert({
      title: t('排查链接'),
      content: (
        <div className="text-left">
          <p className="text-xs text-gray-500 mb-2">请复制以下链接进行排查：</p>
          <div 
            className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-sm text-blue-600 break-all select-all cursor-text"
            onClick={(e) => {
              // 选中文本
              const selection = window.getSelection();
              const range = document.createRange();
              range.selectNodeContents(e.currentTarget);
              selection?.removeAllRanges();
              selection?.addRange(range);
            }}
          >
            {debugUrl}
          </div>
          <p className="text-xs text-gray-400 mt-2">💡 点击链接可自动全选，然后按 Cmd+C (Mac) 或 Ctrl+C (Windows) 复制</p>
        </div>
      ),
      confirmText: t('知道了'),
    });
  }, [session, t]);
  
  // 前往排查页面
  const handleGoToDebug = useCallback(() => {
    if (!session?.id) {
      Toast.show({ content: t('会话未创建'), icon: 'fail' });
      return;
    }
    
    navigate(`/ai-training/debug?id=${session.id}`);
  }, [session, navigate, t]);
  
  // 构建自动回复提示词
  const buildAutoReplyPrompt = () => {
    let prompt = `你是一个AI陪练学员，需要根据以下信息扮演角色并生成回复：\n\n`;
    
    if (studentInfo) {
      prompt += `## 学员信息\n`;
      prompt += `姓名: ${studentInfo.name}\n`;
      if (studentInfo.description) {
        prompt += `描述: ${studentInfo.description}\n`;
      }
      if (studentInfo.role_requirements) {
        prompt += `角色要求: ${studentInfo.role_requirements}\n`;
      }
      if (studentInfo.additional_info) {
        prompt += `附加信息: ${studentInfo.additional_info}\n`;
      }
    }
    
    prompt += `\n## 对话历史\n`;
    const recentMessages = messages.slice(-10);
    recentMessages.forEach((msg) => {
      const speaker = msg.type === 'user' ? '学员' : 'AI教练';
      prompt += `${speaker}: ${msg.content}\n`;
    });
    
    prompt += `\n请根据以上信息，以学员的身份生成下一条回复消息。\n`;
    prompt += `注意：\n`;
    prompt += `1. 回复要符合学员的角色设定\n`;
    prompt += `2. 回复要自然流畅，不要太过机械\n`;
    prompt += `3. 回复长度控制在50-200字之间\n`;
    prompt += `4. 直接返回回复内容，不要包含任何前缀或说明\n`;
    
    return prompt;
  };

  // 自动回复
  const handleAutoReply = async () => {
    if (!session?.id || !studentInfo || isAutoReplying) return;
    
    setIsAutoReplying(true);
    try {
      const prompt = buildAutoReplyPrompt();
      
      const response = await request('/scene-students/ai/generate-reply', {
        method: 'POST',
        data: {
          prompt: prompt,
        },
      });
      
      if (response.success && response.data && response.data.reply) {
        // 模拟输入
        setInputText(response.data.reply);
        
        // 延迟发送，模拟打字
        await sleep(1000);
        
        // 自动发送
        const messageId = uuidv4();
        const userMessage: Message = {
          id: messageId,
          type: "user",
          content: response.data.reply,
          isComplete: true,
          timestamp: Date.now(),
        };
        
        setMessages((draft) => {
          draft.push(userMessage);
        });
        
        setInputText('');
        
        setTimeout(() => {
          scrollToBottom();
        }, 100);
        
        setTimeout(async () => {
          setShowLoading(true);
          await sleep(100);
          scrollToBottom();
        }, 200);
        
        const messageData = {
          type: "chat_message",
          data: {
            content: response.data.reply,
            streaming: true,
            message_type: "text",
            session_id: session.id,
          },
          session_id: session.id,
          message_id: messageId,
        };
        
        console.log("学员自动发送WebSocket消息:", messageData);
        sendWsMessage(messageData);
      }
    } catch (error) {
      console.error('自动回复失败:', error);
    } finally {
      setIsAutoReplying(false);
    }
  };
  
  // 监听AI消息完成，触发自动回复
  useEffect(() => {
    if (!autoReplyEnabled || !student_id || !session) return;
    
    // 清除之前的定时器
    if (autoReplyTimerRef.current) {
      clearTimeout(autoReplyTimerRef.current);
      autoReplyTimerRef.current = null;
    }
    
    // 检查最后一条消息是否是AI消息且已完成
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && 
        lastMessage.type === 'ai' && 
        lastMessage.isComplete && 
        !lastMessage.isStreaming &&
        !isAutoReplying) {
      
      // 延迟触发自动回复，模拟真实场景
      autoReplyTimerRef.current = setTimeout(() => {
        handleAutoReply();
      }, 2000);
    }
    
    return () => {
      if (autoReplyTimerRef.current) {
        clearTimeout(autoReplyTimerRef.current);
      }
    };
  }, [messages, autoReplyEnabled, student_id, session, isAutoReplying]);
  
  // 发送消息
  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim() || !session?.id || !isConnected) {
      console.warn("无法发送消息: 会话未创建或WebSocket未连接");
      return;
    }
    
    try {
      const messageId = uuidv4();
      const userMessage: Message = {
        id: messageId,
        type: "user",
        content: inputText.trim(),
        isComplete: true,
        timestamp: Date.now(),
      };
      
      setMessages((draft) => {
        draft.push(userMessage);
      });
      
      setInputText('');
      
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      
      setTimeout(async () => {
        setShowLoading(true);
        await sleep(100);
        scrollToBottom();
      }, 200);
      
      const messageData = {
        type: "chat_message",
        data: {
          content: inputText.trim(),
          streaming: true,
          message_type: "text",
          session_id: session.id,
        },
        session_id: session.id,
        message_id: messageId,
      };
      
      console.log("发送WebSocket消息:", messageData);
      sendWsMessage(messageData);
    } catch (error) {
      console.error('发送消息失败:', error);
      Toast.show({ content: t('发送消息失败'), icon: 'fail' });
    }
  }, [inputText, session, isConnected, sendWsMessage, scrollToBottom]);
  
  // 查看原始提示词
  const handleViewOriginalPrompt = useCallback(async (messageId: string) => {
    setLoadingPrompt(true);
    try {
      const response = await request(`/special-info-display/chat-prompt/${messageId}`, {
         method: 'GET'
       });
       console.log('response',response);
       
      if (response.data) {
        setPromptData(response.data.target_data);
        setShowPromptModal(true);
      } else {
        Toast.show({ content: t('未找到原始提示词信息'), icon: 'fail' });
      }
    } catch (error) {
      console.error('获取原始提示词失败:', error);
      Toast.show({ content: t('获取原始提示词失败'), icon: 'fail' });
    } finally {
      setLoadingPrompt(false);
    }
  }, [inputText, session, isConnected, sendWsMessage, scrollToBottom]);
  
  // 复制原始提示词全部内容
  const copyPromptData = useCallback(() => {
    if (!promptData) {
      Toast.show({ content: t('暂无数据可复制'), icon: 'fail' });
      return;
    }
    
    let copyText = '';
    
    if (promptData.session) {
      copyText += t('会话信息') + ':\n' + JSON.stringify(promptData.session, null, 2) + '\n\n';
    }
    
    if (promptData.complete_prompt) {
      copyText += t('全部内容') + ':\n' + promptData.complete_prompt;
    }
    
    navigator.clipboard.writeText(copyText).then(() => {
      Toast.show({ content: t('内容已复制到剪贴板'), icon: 'success' });
    }).catch(() => {
      Toast.show({ content: t('复制失败，请重试'), icon: 'fail' });
    });
  }, [promptData]);
  
  // 处理回车发送
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // 初始化
  useEffect(() => {
    fetchScenarioDetail();
    if (student_id) {
      fetchStudentInfo();
    }
  }, [scenarioId, student_id]);
  
  // 消息变化时滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-gray-500">{t('加载中...')}</div>
      </div>
    );
  }
  
  if (!scenarioData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-gray-500">{t('场景不存在')}</div>
      </div>
    );
  }
  
  return (
    <div className="h-screen bg-gray-50 flex flex-col relative overflow-hidden">
      {!session ? (
        <div className="flex-1 flex flex-col items-center justify-center p-4 bg-gradient-to-b from-blue-50 to-purple-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
            <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">{scenarioData.name}</h1>
            <p className="text-gray-500 text-center mb-8">
              {student_id ? '🎓 学员训练模式' : '预览模式'}
            </p>
            
           
            
            <div className="mb-8">
              <div className="text-sm font-medium text-gray-700 mb-4">{t('选择角色')}</div>
              <div className="grid grid-cols-2 gap-3">
                {scenarioData.ai_roles?.map((role) => (
                  <div
                    key={role.scenario_role_id}
                    onClick={() => setSelectedRoleId(role.scenario_role_id)}
                    className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                      selectedRoleId === role.scenario_role_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Avatar
                      src={role.role_data.avatar || ''}
                      fallback={role.role_data.name.charAt(0)}
                      style={{
                        '--size': '56px',
                        backgroundColor: selectedRoleId === role.scenario_role_id ? '#3b82f6' : '#f3f4f6',
                      }}
                    />
                    <div className="text-sm font-medium text-gray-800 text-center truncate w-full">
                      {role.role_data.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <Button
              block
              size="large"
              color="primary"
              loading={isConnecting}
              disabled={!selectedRoleId}
              onClick={createSession}
              className="bg-gradient-to-r from-blue-600 to-purple-600 border-none h-12 text-lg font-semibold shadow-lg shadow-blue-500/20 rounded-xl"
            >
              {isConnecting ? t('连接中...') : t('开始对话')}
            </Button>
             {studentInfo && (
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="text-sm font-medium text-blue-800 mb-2">
                  🎓 学员信息
                </div>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><strong>学员：</strong>{studentInfo.name}</p>
                  {studentInfo.description && (
                    <p><strong>描述：</strong>{studentInfo.description}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-gray-600">自动回复</span>
                    <Switch
                      checked={autoReplyEnabled}
                      onChange={setAutoReplyEnabled}
                    />
                  </div>
                  {autoReplyEnabled && (
                    <p className="text-xs text-green-600 mt-2">
                      ✓ AI将根据学员角色自动生成回复
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* 头部 */}
          <div className="flex-none h-14 bg-white border-b border-gray-100 px-4 flex items-center justify-between shadow-sm z-10">
            <div className="flex items-center gap-3">
              {selectedRoleId && (
                <Avatar
                  src={scenarioData.ai_roles?.find(r => r.scenario_role_id === selectedRoleId)?.role_data.avatar || ''}
                  fallback={scenarioData.ai_roles?.find(r => r.scenario_role_id === selectedRoleId)?.role_data.name.charAt(0) || ''}
                  style={{ '--size': '32px' }}
                />
              )}
              <div>
                <div className="font-medium text-gray-800 flex items-center gap-2">
                  {scenarioData.ai_roles?.find(r => r.scenario_role_id === selectedRoleId)?.role_data.name}
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  {isSessionEnded && (
                    <span className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-600 ml-1">
                      已结束
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {studentInfo && !isSessionEnded && (
                <>
                  <div className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700">
                    🎓 {studentInfo.name}
                  </div>
                  <Button
                    size="small"
                    color={autoReplyEnabled ? 'success' : 'default'}
                    fill={autoReplyEnabled ? 'solid' : 'outline'}
                    onClick={() => setAutoReplyEnabled(!autoReplyEnabled)}
                    loading={isAutoReplying}
                    disabled={isSessionEnded}
                    className="text-xs px-3 py-1"
                  >
                    {isAutoReplying ? '回复中...' : autoReplyEnabled ? '🤖 自动回复中' : '▶️ 启动自动回复'}
                  </Button>
                </>
              )}
              <Button
                fill="outline"
                size="small"
                color="primary"
                onClick={handleCopyDebugLink}
                className="text-xs px-3 py-1"
              >
                📋 {t('复制排查链接')}
              </Button>
              <Button
                fill="solid"
                size="small"
                color="primary"
                onClick={handleGoToDebug}
                className="text-xs px-3 py-1"
              >
                🔍 {t('前往排查')}
              </Button>
              {!isSessionEnded && (
                <Button
                  fill="solid"
                  size="small"
                  color="danger"
                  onClick={handleFinishSession}
                  disabled={!isConnected}
                  className="text-xs px-3 py-1"
                >
                  {t('结束对话')}
                </Button>
              )}
              <Button
                fill="none"
                size="small"
                onClick={restartConversation}
                className="text-gray-600 hover:text-blue-600"
              >
                <RedoOutline className="mr-1" />
                <span className="text-xs">{t('重新开始')}</span>
              </Button>
            </div>
          </div>

          {/* 消息区域 */}
          <div className="flex-1 overflow-hidden relative bg-gray-50">
            <div 
               ref={chatContentRef}
               className="h-full overflow-y-auto p-4 space-y-6 pb-24"
             >
               {messages.map((message) => (
                 <motion.div
                   key={message.id}
                   initial={{ opacity: 0, y: 10 }}
                   animate={{ opacity: 1, y: 0 }}
                   className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                 >
                   <div className={`flex max-w-[85%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'} items-start gap-2`}>
                     {message.type === 'ai' && (
                        <Avatar
                          src={scenarioData.ai_roles?.find(r => r.scenario_role_id === selectedRoleId)?.role_data.avatar || ''}
                          fallback={scenarioData.ai_roles?.find(r => r.scenario_role_id === selectedRoleId)?.role_data.name.charAt(0) || ''}
                          style={{ '--size': '36px' }}
                          className="mt-1 flex-shrink-0"
                        />
                     )}
                     
                     <div
                       className={`px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm ${
                         message.type === 'user'
                           ? 'bg-blue-600 text-white rounded-tr-none'
                           : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                       }`}
                     >
                       <div className="whitespace-pre-wrap break-words">
                         {message.content}
                         {message.isStreaming && (
                           <span className="inline-block w-1.5 h-4 bg-current opacity-75 animate-pulse ml-1 align-middle"></span>
                         )}
                       </div>
                       
                       {message.type === 'ai' && !message.isStreaming && !student_id && (
                         <div className="mt-2 pt-2 border-t border-gray-100 flex justify-end">
                          <button
                            onClick={() => handleViewOriginalPrompt(message.id)}
                            className="text-xs text-blue-500 hover:text-blue-700 opacity-70 hover:opacity-100 transition-opacity"
                          >
                            {t('查看Prompt')}
                          </button>
                         </div>
                       )}
                     </div>
                   </div>
                 </motion.div>
               ))}
               
               {showLoading && (
                 <motion.div
                   initial={{ opacity: 0 }}
                   animate={{ opacity: 1 }}
                   className="flex justify-start items-center gap-2 pl-12"
                 >
                    <div className="bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100">
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                 </motion.div>
               )}
             </div>
          </div>

          {/* 底部输入 */}
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 shadow-lg z-20">
             {isSessionEnded && (
               <div className="max-w-4xl mx-auto mb-2 text-center">
                 <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-full text-xs">
                   <span>对话已结束，点击"重新开始"可开启新的对话</span>
                 </div>
               </div>
             )}
             {!isSessionEnded && autoReplyEnabled && isAutoReplying && (
               <div className="max-w-4xl mx-auto mb-2 text-center">
                 <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-xs">
                   <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                   AI学员正在思考并生成回复...
                 </div>
               </div>
             )}
             <div className="max-w-4xl mx-auto flex gap-3 items-end">
              <TextArea
                value={inputText}
                onChange={(val) => setInputText(val)}
                onKeyDown={handleKeyPress}
                placeholder={
                  isSessionEnded 
                    ? '对话已结束' 
                    : autoReplyEnabled 
                      ? (isAutoReplying ? 'AI学员正在自动回复...' : '自动回复模式（可手动输入）') 
                      : '输入消息...'
                }
                disabled={!isConnected || isAutoReplying || isSessionEnded}
                 autoSize={{ minRows: 1, maxRows: 4 }}
                 className="flex-1 bg-gray-50 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-blue-100 transition-all"
               />
               <Button
                 color="primary"
                 onClick={handleSendMessage}
                 disabled={!inputText.trim() || !isConnected || isAutoReplying || isSessionEnded}
                 className="rounded-xl h-10 px-6 font-medium shadow-blue-200 shadow-md"
                 style={{
                   backgroundColor: (!inputText.trim() || !isConnected || isAutoReplying || isSessionEnded) ? '#e5e7eb' : '#2563eb',
                   border: 'none',
                  color: 'white'
                }}
              >
                {t('发送')}
              </Button>
             </div>
          </div>
        </>
      )}
       
       {/* 原始提示词Modal */}
       <Modal
         visible={showPromptModal}
         onClose={() => setShowPromptModal(false)}
         title={t('原始提示词信息')}
         content={
           <div className="space-y-4 max-h-[60vh] overflow-y-auto">
             {promptData && (
               <div className="space-y-4">
                 {promptData.session && (
                   <div>
                     <h4 className="font-semibold text-gray-700 mb-2 text-xs uppercase tracking-wider">{t('会话信息')}</h4>
                     <pre className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600 overflow-auto border border-gray-100">
                       {JSON.stringify(promptData.session, null, 2)}
                     </pre>
                   </div>
                 )}
                 
                 {promptData.complete_prompt && (
                   <div>
                     <h4 className="font-semibold text-gray-700 mb-2 text-xs uppercase tracking-wider">{t('完整Prompt')}</h4>
                     <div className="bg-blue-50 p-3 rounded-lg text-sm text-gray-700 border border-blue-100 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(promptData.complete_prompt || '') }} />
                   </div>
                 )}
               </div>
             )}
             {loadingPrompt && (
               <div className="text-center py-8">
                 <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                 <div className="text-gray-400 text-sm">{t('加载中...')}</div>
               </div>
             )}
           </div>
         }
         actions={[
           {
             key: 'copy',
             text: t('复制内容'),
             onClick: copyPromptData
           },
           {
             key: 'close',
             text: t('关闭'),
             onClick: () => setShowPromptModal(false)
           }
         ]}
       />
    </div>
  );
};

export default AiTrainingPreview;

